import { useMemo } from 'react';
import { z } from 'zod';

import type { Communication } from '@/api/fe-api/entities/communication';
import {
  codec,
  type PushAndReplace,
  useQueryParams,
} from '@/hooks/router/use-query-params';

import { useCurrentSite } from './use-current-site';

export type ListFilters = {
  search?: string;
  type?: Communication['type'];
  siteIds?: string[];
};

export const filtersEmptyState: ListFilters = {
  search: undefined,
  type: undefined,
  siteIds: undefined,
};

export type ListFiltersStatus = {
  active: boolean;
};

export function useListFilters(): [
  ListFilters,
  PushAndReplace<ListFilters>,
  ListFiltersStatus,
] {
  const site = useCurrentSite();
  const [input, history] = useQueryParams({
    search: {
      key: 's',
      schema: z.string().optional(),
      codec: codec.string,
    },
    type: {
      key: 'type',
      schema: z.enum(['meeting', 'email']).optional(),
      codec: codec.string,
    },
    siteIds: {
      key: 'sites',
      schema: z.array(z.string()).optional(),
      codec: codec.array(codec.string, '_'),
    },
  });

  const status = useMemo<ListFiltersStatus>(
    () => ({
      active: Boolean(
        input.type ||
        input.search ||
        (!site && input.siteIds && input.siteIds.length > 0),
      ),
    }),
    [input, site],
  );

  return [input, history, status] as const;
}
